import { useRef, useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { SEND_PUSH_NOTIFICATION } from "../constants/api";
import {
  ChatDoctorAvatarIcon,
  ChatPatientAvatarIcon,
  ChatSendIcon,
} from "./icons/chatMessageIcons";

interface Message {
  text: string;
  from: "doctor" | "patient";
  createdAt?: any;
}

interface ChatWidgetProps {
  patientId: string;
  doctorId: string;
  onClose: () => void;
  fullHeight?: boolean;
  /** When true, omits outer card chrome (for use inside patient details tab panel). */
  embedded?: boolean;
}

function PatientChatAvatar() {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white"
      aria-hidden
    >
      <ChatDoctorAvatarIcon className="h-5 w-5" />
    </div>
  );
}

function ClinicianChatAvatar() {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white"
      aria-hidden
    >
      <ChatPatientAvatarIcon className="h-5 w-5" />
    </div>
  );
}

export default function ChatWidget({
  patientId,
  doctorId,
  embedded = false,
  fullHeight = false,
}: ChatWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  // Listen for real-time updates
  useEffect(() => {
    const chatId = `${patientId}_${doctorId}`;
    const q = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((doc) => ({ ...doc.data() } as unknown as Message))
      );
    });
    return () => unsubscribe();
  }, [patientId, doctorId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (input.trim() === "") return;
    const chatId = `${patientId}_${doctorId}`;
    const messageText = input.trim();

    try {
      // Send message to Firebase
      await addDoc(collection(db, "chats", chatId, "messages"), {
        text: messageText,
        from: "doctor", // when sending the message from the mobile app use from: "patient"
        createdAt: serverTimestamp(),
      });

      setInput("");

      // Send push notification after successful message save
      try {
        const response = await fetch(SEND_PUSH_NOTIFICATION, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({
            userId: patientId,
            message: messageText,
          }),
        });

        if (!response.ok) {
          console.error(
            "Failed to send push notification:",
            response.statusText
          );
        }
      } catch (notificationError) {
        console.error("Error sending push notification:", notificationError);
        // Don't block the UI if notification fails
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Restore input if message sending failed
      setInput(messageText);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
  };

  const rootClassName =
    embedded && fullHeight
      ? "flex h-full min-h-0 flex-1 flex-col"
      : embedded
        ? "flex min-h-[400px] flex-col"
        : "flex h-full flex-col rounded-xl bg-white shadow-sm";

  return (
    <div className={rootClassName}>
      {!embedded && (
        <div className="flex items-center gap-2 border-b border-guardian-20 px-4 py-3">
          <img src="/images/chat_logo.png" alt="" className="h-5 w-5" aria-hidden />
          <span className="text-base font-bold text-guardian-body">Chat</span>
        </div>
      )}
      {/* Messages */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white">
        <div className="flex flex-col justify-end gap-4 px-4 py-4">
          {messages.map((msg, idx) => {
            const isDoctor = msg.from === "doctor";
            return (
              <div
                key={idx}
                className={
                  isDoctor
                    ? "pl-12 max-xl:pl-4"
                    : "pr-12 max-xl:pr-4"
                }
              >
                <div
                  className={`w-full rounded-2xl p-3 ${
                    isDoctor ? "bg-[#CCFBF1]" : "bg-guardian-10"
                  }`}
                >
                  <div
                    className={`flex gap-3 ${
                      isDoctor ? "items-start" : "items-center"
                    }`}
                  >
                    {!isDoctor && <PatientChatAvatar />}
                    <span
                      className={`break-words text-sm leading-5 tracking-[-0.013em] ${
                        isDoctor
                          ? "min-w-0 flex-1 font-semibold text-guardian-80"
                          : "shrink-0 font-normal text-[#02040A]"
                      }`}
                    >
                      {msg.text}
                    </span>
                    {isDoctor && <ClinicianChatAvatar />}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>
      {/* Input */}
      <div className="flex shrink-0 items-end gap-2 px-4 pb-4">
        <input
          type="text"
          className="min-h-[48px] flex-1 rounded-2xl border-none bg-guardian-10 px-4 py-3 text-base tracking-[-0.015em] text-guardian-body placeholder:text-guardian-header focus:outline-none"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
        />
        <button
          type="button"
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-rag-green disabled:opacity-50"
          onClick={handleSend}
          disabled={input.trim() === ""}
          aria-label="Send message"
        >
          <ChatSendIcon className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
